import { useEffect, useState } from 'react'
import {
  Loader2,
  Trash2,
  Shield,
  Link,
  Unlink,
  ChevronDown,
  Wrench,
} from 'lucide-react'
import { api } from '../../services/api'
import { useGameStore } from '../../store/gameStore'
import type { Mod, ModType, ModRarity, UnitStats } from '@territory-nexus/shared'
import { MOD_META, RARITY_CONFIG, UNIT_META } from '@territory-nexus/shared'
import clsx from 'clsx'

export default function ModsPanel() {
  const { units, fetchUnits } = useGameStore()
  const [mods, setMods] = useState<Mod[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [equipTarget, setEquipTarget] = useState<string | null>(null) // modId being equipped

  const fetchMods = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get<{ success: boolean; data: Mod[] }>('/mods')
      if (res.data.success) {
        setMods(res.data.data)
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to load mods')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMods()
    fetchUnits()
  }, [fetchUnits])

  const handleEquip = async (modId: string, unitId: string) => {
    setActionLoading(`equip-${modId}`)
    try {
      await api.post(`/mods/${modId}/equip`, { unitId })
      await Promise.all([fetchMods(), fetchUnits()])
      setEquipTarget(null)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to equip mod')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const handleUnequip = async (modId: string) => {
    setActionLoading(`unequip-${modId}`)
    try {
      await api.post(`/mods/${modId}/unequip`)
      await Promise.all([fetchMods(), fetchUnits()])
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to unequip mod')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const handleScrap = async (modId: string) => {
    if (!confirm('Scrap this mod permanently?')) return
    setActionLoading(`scrap-${modId}`)
    try {
      await api.delete(`/mods/${modId}`)
      await fetchMods()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to scrap mod')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const formatStatBonuses = (bonuses: Partial<UnitStats>): string => {
    const statLabels: Record<keyof UnitStats, string> = {
      hp: 'HP', atk: 'ATK', def: 'DEF', spd: 'SPD', acc: 'ACC', energy: 'NRG',
    }
    return Object.entries(bonuses)
      .filter(([, v]) => v && v > 0)
      .map(([k, v]) => `+${v} ${statLabels[k as keyof UnitStats]}`)
      .join(', ')
  }

  const getUnitName = (unitId: string): string => {
    const unit = units.find(u => u.id === unitId)
    if (!unit) return 'Unknown'
    const meta = UNIT_META[unit.unitType]
    return `${meta.shortName} Lv.${unit.level}`
  }

  // Group mods: equipped first, then by rarity
  const rarityOrder: ModRarity[] = ['legendary', 'rare', 'common']
  const equippedMods = mods.filter(m => m.equippedTo)
  const inventoryMods = mods
    .filter(m => !m.equippedTo)
    .sort((a, b) => rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity))

  // Available units for equipping (not in battle)
  const availableUnits = units.filter(u => u.status !== 'in_battle')

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-bold text-white">Mods</h2>
          <span className="text-sm text-slate-400">{mods.length} total</span>
        </div>

        {error && (
          <div className="glass border border-red-500/30 bg-red-500/10 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <p className="text-xs text-slate-500">
          Win battles to earn mod drops. Equip mods to boost your units' stats.
        </p>

        {mods.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center border border-white/5">
            <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 mb-2">No mods yet</p>
            <p className="text-slate-500 text-sm">
              Win battles for a 30% chance to earn a mod drop!
            </p>
          </div>
        ) : (
          <>
            {/* Equipped Mods Section */}
            {equippedMods.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">
                  Equipped ({equippedMods.length})
                </h3>
                <div className="space-y-2">
                  {equippedMods.map(mod => (
                    <ModCard
                      key={mod.id}
                      mod={mod}
                      unitName={mod.equippedTo ? getUnitName(mod.equippedTo) : null}
                      actionLoading={actionLoading}
                      onUnequip={handleUnequip}
                      formatStatBonuses={formatStatBonuses}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Inventory Section */}
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Inventory ({inventoryMods.length})
              </h3>
              {inventoryMods.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">All mods equipped</p>
              ) : (
                <div className="space-y-2">
                  {inventoryMods.map(mod => (
                    <div key={mod.id}>
                      <ModCard
                        mod={mod}
                        unitName={null}
                        actionLoading={actionLoading}
                        onScrap={handleScrap}
                        onStartEquip={() => setEquipTarget(equipTarget === mod.id ? null : mod.id)}
                        isEquipOpen={equipTarget === mod.id}
                        formatStatBonuses={formatStatBonuses}
                      />
                      {/* Equip Unit Selector */}
                      {equipTarget === mod.id && (
                        <div className="mt-1 ml-4 p-2 rounded-lg bg-slate-800/80 border border-white/5 space-y-1">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Select unit</p>
                          {availableUnits.length === 0 ? (
                            <p className="text-xs text-slate-500">No units available</p>
                          ) : (
                            availableUnits.map(unit => {
                              const meta = UNIT_META[unit.unitType]
                              const isLoading = actionLoading === `equip-${mod.id}`
                              return (
                                <button
                                  key={unit.id}
                                  onClick={() => handleEquip(mod.id, unit.id)}
                                  disabled={isLoading}
                                  className="w-full flex items-center gap-2 p-1.5 rounded-md hover:bg-white/10 text-left transition disabled:opacity-50"
                                >
                                  {isLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
                                  ) : (
                                    <Link className="w-3 h-3 text-cyan-400" />
                                  )}
                                  <span className={clsx('text-xs font-medium', meta.color)}>
                                    {meta.shortName}
                                  </span>
                                  <span className="text-[10px] text-slate-500">Lv.{unit.level}</span>
                                </button>
                              )
                            })
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Extracted ModCard component
function ModCard({
  mod,
  unitName,
  actionLoading,
  onUnequip,
  onScrap,
  onStartEquip,
  isEquipOpen,
  formatStatBonuses,
}: {
  mod: Mod
  unitName: string | null
  actionLoading: string | null
  onUnequip?: (modId: string) => void
  onScrap?: (modId: string) => void
  onStartEquip?: () => void
  isEquipOpen?: boolean
  formatStatBonuses: (bonuses: Partial<UnitStats>) => string
}) {
  const modMeta = MOD_META[mod.modType]
  const rarityConfig = RARITY_CONFIG[mod.rarity]
  const isLoading = actionLoading?.includes(mod.id)

  return (
    <div
      className={clsx(
        'glass rounded-lg border p-3 transition',
        rarityConfig.border,
      )}
    >
      <div className="flex items-center gap-3">
        {/* Mod Icon */}
        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center text-lg', rarityConfig.bg)}>
          {modMeta.icon}
        </div>

        {/* Mod Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={clsx('text-sm font-medium', modMeta.color)}>
              {modMeta.shortName}
            </span>
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', rarityConfig.bg, rarityConfig.color)}>
              {rarityConfig.name}
            </span>
          </div>
          <p className="text-xs text-emerald-400 mt-0.5">
            {formatStatBonuses(mod.statBonuses)}
          </p>
          {unitName && (
            <p className="text-[10px] text-slate-500 mt-0.5">
              Equipped on {unitName}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {mod.equippedTo && onUnequip && (
            <button
              onClick={() => onUnequip(mod.id)}
              disabled={!!isLoading}
              className="p-1.5 rounded-md hover:bg-amber-500/20 text-amber-400 transition disabled:opacity-50"
              title="Unequip"
            >
              {actionLoading === `unequip-${mod.id}` ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
            </button>
          )}
          {!mod.equippedTo && onStartEquip && (
            <button
              onClick={onStartEquip}
              className={clsx(
                'p-1.5 rounded-md transition',
                isEquipOpen
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'hover:bg-cyan-500/20 text-cyan-400'
              )}
              title="Equip to unit"
            >
              <ChevronDown className={clsx('w-4 h-4 transition-transform', isEquipOpen && 'rotate-180')} />
            </button>
          )}
          {!mod.equippedTo && onScrap && (
            <button
              onClick={() => onScrap(mod.id)}
              disabled={!!isLoading}
              className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400 transition disabled:opacity-50"
              title="Scrap"
            >
              {actionLoading === `scrap-${mod.id}` ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
